"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, FileCheck2, MapPin, PhoneCall, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface SpecItem {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  detail: string;
}
const specItems: SpecItem[] = [{
  id: "spec-1",
  icon: ShieldCheck,
  title: "Direct local technician response",
  detail: "Zero third-party brokers. TriServe crews own every stage of mitigation and structural recovery."
}, {
  id: "spec-2",
  icon: FileCheck2,
  title: "Direct insurance coordination",
  detail: "We document thermal imaging and moisture readings, filing directly with your adjuster to protect your claim."
}, {
  id: "spec-3",
  icon: Clock,
  title: "24/7 immediate regional dispatch",
  detail: "Fully outfitted extraction and remediation trucks on standby across MD, DC, and Northern Virginia."
}];
export default function WhyChoose() {
  const prefersReducedMotion = useReducedMotion();
  const motionInitial = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = prefersReducedMotion ? {
    duration: 0.01
  } : {
    duration: 0.35,
    ease: "easeOut"
  };
  return <section id="why-choose-triserve" data-nav="dark" className="dark bg-background text-foreground py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Grounded Secondary Purple Structural Box */}
        <div className="relative overflow-hidden rounded-2xl border border-border/30 bg-secondary text-secondary-foreground shadow-2xl p-6 sm:p-10 lg:p-12">
          {/* Subtle Ambient Depth */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
            {/* Left Column: Documentary Image with Equipment Spec Framing */}
            <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
            once: true,
            amount: 0.2
          }} transition={motionTransition} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-xl border border-secondary-foreground/20 bg-background/5 shadow-xl">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_response_team.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_response_team.png-wvc-sizes" alt="TriServe liveried emergency restoration truck and certified technicians staged on a regional job site" loading="lazy" decoding="async" className="aspect-4/3 w-full object-cover object-center sm:aspect-16/11 lg:aspect-4/5" src="https://triserveinc.com/wp-content/uploads/TriServe-Emergency-Fire-Damage-Cleanup-–-Fast-Response.png" />

                {/* Inset Equipment Spec Tag */}
                <div className="absolute bottom-3 left-3 right-3 rounded-md border border-white/15 bg-black/80 px-3.5 py-2.5 backdrop-blur-md">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
                      </span>
                      <span className="font-mono text-xs font-semibold tracking-wide text-white uppercase">
                        Unit #04 • Active Deployment
                      </span>
                    </div>
                    <span className="hidden font-mono text-2xs text-white/70 sm:inline-block">
                      MD • DC • NOVA
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Heading, Narrative, and Spec Badges */}
            <div className="lg:col-span-7">
              <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              ...motionTransition,
              delay: prefersReducedMotion ? 0 : 0.05
            }} className="space-y-4">
                <div className="flex flex-wrap items-center gap-2.5">
                  <Badge variant="outline" className="border-accent/40 bg-accent/10 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-accent">
                    Local Crew Dispatch
                  </Badge>
                  <span className="inline-flex items-center gap-1 font-mono text-xs text-secondary-foreground/75">
                    <MapPin className="h-3.5 w-3.5 text-accent" />
                    Maryland • Washington DC • Northern Virginia
                  </span>
                </div>

                <h2 className="font-default text-3xl font-bold tracking-tight text-secondary-foreground sm:text-4xl lg:text-[2.6rem] lg:leading-tight">
                  Direct local crews, not an out-of-state call center.
                </h2>

                <p className="text-base leading-relaxed text-secondary-foreground/85 sm:text-lg">
                  When water floods a basement or smoke fills a building, you speak with the specialists who actually manage your restoration. TriServe operates local Mid-Atlantic response teams who handle site stabilization, communicate directly with insurance adjusters, and stay with your project from emergency extraction through complete structural rebuild.
                </p>
              </motion.div>

              {/* Technical Spec Badges */}
              <div className="mt-8 grid grid-cols-1 gap-3.5 sm:grid-cols-3">
                {specItems.map((spec, i) => {
                const Icon = spec.icon;
                return <motion.div key={spec.id} data-index={i} initial={motionInitial} whileInView={motionAnimate} viewport={{
                  once: true,
                  amount: 0.2
                }} transition={{
                  ...motionTransition,
                  delay: prefersReducedMotion ? 0 : 0.08 * (i + 1)
                }} className="group rounded-lg border border-secondary-foreground/15 bg-secondary-foreground/5 p-4 transition-[border-color,background-color] duration-200 hover:border-accent/40 hover:bg-secondary-foreground/10">
                      <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-md border border-accent/20 bg-accent/15 text-accent">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-default text-sm font-semibold text-secondary-foreground">
                        {spec.title}
                      </h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-secondary-foreground/75">
                        {spec.detail}
                      </p>
                    </motion.div>;
              })}
              </div>

              {/* Call to Action Bar */}
              <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              ...motionTransition,
              delay: prefersReducedMotion ? 0 : 0.2
            }} className="mt-8 flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
                <Button asChild size="lg" className="bg-accent text-accent-foreground shadow-md hover:bg-accent/90 font-bold">
                  <Link to="/ContactRequestServicePage">
                    <span>Request emergency service</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
                  <a href="tel:855-435-6631">
                    <PhoneCall className="h-4 w-4 text-accent" />
                    <span>Call 855-435-6631</span>
                  </a>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}